import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { 
    FaSearch, FaFileInvoice, FaEdit, FaSave, FaTimes, FaPlusCircle, 
    FaFilePdf, FaHistory, FaCalendarAlt, FaExclamationCircle, FaTrash, FaCheckCircle
} from 'react-icons/fa';
import { 
    fetchProformas, createProforma, updateProforma, deleteProforma, fetchClients, 
    fetchPrestations, exportProformaPDF 
} from '../api'; 

const ProformaPage = ({ user }) => {
    const location = useLocation(); 
    
    const [proformas, setProformas] = useState([]);
    const [clients, setClients] = useState([]);
    const [prestations, setPrestations] = useState([]);
    
    const [clientSearch, setClientSearch] = useState('');
    const [dateEmission, setDateEmission] = useState(new Date().toISOString().split('T')[0]);
    const [currentLines, setCurrentLines] = useState([]);
    const [editingId, setEditingId] = useState(null);
    
    const [prestationSearchTerm, setPrestationSearchTerm] = useState(''); 
    const [showSuggestions, setShowSuggestions] = useState(false);        
    const [selectedPrestation, setSelectedPrestation] = useState(null);   

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [search, setSearch] = useState("");

    // Vérification du rôle uniquement pour la suppression si besoin
    const userRole = user?.role?.toLowerCase() || '';
    const isAdmin = userRole === 'admin' || userRole === 'superadmin';

    const loadData = async () => {
        setLoading(true);
        try {
            const [resProf, resCli, resPrest] = await Promise.all([
                fetchProformas(), fetchClients(), fetchPrestations()
            ]);
            setProformas(resProf.data);
            setClients(resCli.data);
            setPrestations(resPrest.data);
        } catch (err) { 
            setMessage({ type: 'error', text: 'Erreur chargement données' }); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => { 
        loadData(); 
        if (location.state && location.state.incomingPrestations) {
            const linesFromTransfer = location.state.incomingPrestations.map(p => ({
                prestationId: p._id, code: p.code, name: p.name, details: p.details || '',
                price: p.price, quantity: 1, total: p.price
            }));
            setCurrentLines(linesFromTransfer);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const filteredPrestations = prestationSearchTerm.length > 0
        ? prestations.filter(p => p.name.toLowerCase().includes(prestationSearchTerm.toLowerCase()) || p.code.toLowerCase().includes(prestationSearchTerm.toLowerCase())).slice(0, 50) 
        : [];

    const handleSelectSuggestion = (p) => {
        setSelectedPrestation(p);          
        setPrestationSearchTerm(p.name);    
        setShowSuggestions(false);          
    };

    const handleAddPrestation = () => {
        if (!selectedPrestation) return;
        const newLine = { 
            prestationId: selectedPrestation._id, code: selectedPrestation.code, name: selectedPrestation.name, 
            details: selectedPrestation.details || '', price: Number(selectedPrestation.price), 
            quantity: 1, total: Number(selectedPrestation.price) 
        };
        setCurrentLines([...currentLines, newLine]);
        setSelectedPrestation(null); 
        setPrestationSearchTerm('');
    };

    const updateLine = (index, field, value) => {
        const newLines = [...currentLines];
        newLines[index][field] = value;
        if (field === 'price' || field === 'quantity') {
            newLines[index].total = Number(newLines[index].price) * Number(newLines[index].quantity);
        }
        setCurrentLines(newLines);
    };

    const removeLine = (idx) => setCurrentLines(currentLines.filter((_, i) => i !== idx));

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // La restriction de rôle a été retirée : tous les utilisateurs peuvent modifier ou créer
        if (!clientSearch || currentLines.length === 0) return;

        const payload = { 
            clientName: clientSearch, 
            lines: currentLines, 
            type: 'proforma', 
            date: dateEmission 
        };

        try {
            if (editingId) {
                await updateProforma(editingId, payload);
                setMessage({ type: 'success', text: '✅ Proforma mise à jour avec succès' });
            } else {
                await createProforma(payload);
                setMessage({ type: 'success', text: '✅ Proforma enregistrée avec succès' });
            }
            
            setEditingId(null); 
            setClientSearch(''); 
            setCurrentLines([]); 
            setDateEmission(new Date().toISOString().split('T')[0]); 
            loadData();
            setTimeout(() => setMessage(null), 3000);
        } catch (err) { 
            const errorMsg = err.response?.data?.message || 'Erreur lors de l\'enregistrement';
            setMessage({ type: 'error', text: errorMsg }); 
        }
    };

    const handleDownloadPDF = async (id, number) => {
        try {
            const res = await exportProformaPDF(id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a'); 
            link.href = url;
            const safeFileName = `${number.replace(/\//g, '_')}.pdf`;
            link.setAttribute('download', safeFileName);
            document.body.appendChild(link); 
            link.click();
            link.remove(); 
            window.URL.revokeObjectURL(url);
        } catch (err) { 
            setMessage({ type: 'error', text: "Erreur lors du téléchargement du PDF" }); 
        }
    };

    const handleDelete = async (id) => {
        // La suppression reste protégée pour les administrateurs
        if (!isAdmin) {
            setMessage({ type: 'error', text: "⛔ ACCÈS REFUSÉ : Seul l'administrateur peut supprimer." });
            return;
        }
        if (!window.confirm("⚠️ Voulez-vous vraiment supprimer cette proforma ?")) return;
        try {
            await deleteProforma(id);
            loadData();
            setMessage({ type: 'success', text: '✅ Proforma supprimée avec succès.' });
        } catch (err) {
            setMessage({ type: 'error', text: 'Erreur lors de la suppression.' });
        }
    };

    const totalHT = currentLines.reduce((acc, line) => acc + (line.total || 0), 0);
    const filteredHistory = proformas.filter(p => 
        p.number.toLowerCase().includes(search.toLowerCase()) || 
        p.clientName.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2">
                <div>
                    <h4 className="fw-bold mb-0 text-uppercase text-dark" style={{ letterSpacing: '1px' }}>
                        <FaFileInvoice className="me-2 text-success"/> FACTURES PROFORMAS
                    </h4>
                    <p className="small mb-0 mt-1 text-muted" style={{ letterSpacing: '1px' }}>Édition de devis et simulations de facturation</p>
                </div>
            </div>

            {/* MESSAGES D'ALERTE */}
            {message && (
                <div className={`alert py-2 shadow-sm border-0 ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
                    {message.text}
                </div>
            )}

            {/* FORMULAIRE DE SAISIE */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-header bg-white py-3 fw-bold text-uppercase text-success" style={{ letterSpacing: '1px' }}>
                    {editingId ? "MODIFIER PROFORMA" : "ÉTABLIR UNE PROFORMA"}
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3 mb-4">
                            <div className="col-12 col-md-8">
                                <label className="fw-bold small mb-1 text-muted">CLIENT *</label>
                                <input className="form-control" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} list="clientsList" placeholder="Rechercher client..." required />
                                <datalist id="clientsList">{clients.map(c => <option key={c._id} value={c.name} />)}</datalist>
                            </div>
                            <div className="col-12 col-md-4">
                                <label className="fw-bold small mb-1 text-muted"><FaCalendarAlt className="me-1" /> DATE D'ÉMISSION</label>
                                <input type="date" className="form-control" value={dateEmission} onChange={(e) => setDateEmission(e.target.value)} required />
                            </div>
                        </div>

                        {/* ZONE AJOUT PRESTATION */}
                        <div className="p-3 mb-4 rounded bg-light border">
                            <label className="fw-bold small mb-2 text-success">RECHERCHER UNE PRESTATION</label>
                            <div className="position-relative">
                                <div className="input-group">
                                    <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                                    <input type="text" className="form-control border-start-0" placeholder="Nom ou code..." value={prestationSearchTerm}
                                        onChange={(e) => { setPrestationSearchTerm(e.target.value); setShowSuggestions(true); }}
                                        onFocus={() => setShowSuggestions(true)} onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    />
                                    <button type="button" className={`btn fw-bold px-3 text-white ${selectedPrestation ? 'btn-success' : 'btn-secondary'}`} style={selectedPrestation ? { backgroundColor: '#1A7A38', borderColor: '#1A7A38' } : {}} onClick={handleAddPrestation} disabled={!selectedPrestation}>
                                        <FaPlusCircle className="me-1" /> AJOUTER
                                    </button>
                                </div>
                                {showSuggestions && prestationSearchTerm && (
                                    <div className="position-absolute w-100 shadow bg-white border rounded mt-1" style={{zIndex: 1000, maxHeight: '200px', overflowY: 'auto'}}>
                                        {filteredPrestations.map(p => (
                                            <div key={p._id} className="p-2 border-bottom cursor-pointer text-dark text-decoration-none bg-light-hover" onMouseDown={() => handleSelectSuggestion(p)} style={{cursor: 'pointer'}}>
                                                <div className="fw-bold">{p.name}</div>
                                                <div className="small text-muted">{p.code} | <span className="fw-bold text-success">{p.price.toLocaleString()} F</span></div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="table-responsive mb-4 border rounded">
                            <table className="table table-sm mb-0 align-middle table-hover text-nowrap">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3 py-2 text-muted" style={{minWidth: '100px'}}>Code</th>
                                        <th className="py-2 text-muted" style={{minWidth: '200px'}}>Désignation</th>
                                        <th className="py-2 text-muted" style={{minWidth: '150px'}}>Détails suppl.</th>
                                        <th className="py-2 text-muted" style={{minWidth: '120px'}}>P.U (F)</th>
                                        <th className="py-2 text-muted" style={{minWidth: '80px'}}>Qté</th>
                                        <th className="text-end py-2 text-muted" style={{minWidth: '120px'}}>Total (F)</th>
                                        <th className="text-center py-2 text-muted" style={{minWidth: '50px'}}>#</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentLines.length === 0 ? (
                                        <tr><td colSpan="7" className="text-center py-4 text-muted"><em>Aucune ligne dans cette proforma.</em></td></tr>
                                    ) : (
                                        currentLines.map((line, idx) => (
                                            <tr key={idx}>
                                                <td className="ps-3 text-muted fw-bold small">{line.code}</td>
                                                <td className="fw-bold text-wrap text-dark" style={{minWidth: '200px'}}>{line.name}</td>
                                                <td><input className="form-control form-control-sm" value={line.details} onChange={(e) => updateLine(idx, 'details', e.target.value)} placeholder="..." /></td>
                                                <td><input type="number" className="form-control form-control-sm text-end fw-bold" value={line.price} onChange={(e) => updateLine(idx, 'price', e.target.value)} /></td>
                                                <td><input type="number" className="form-control form-control-sm text-center" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', Number(e.target.value))} /></td>
                                                <td className="text-end fw-bold text-success">{line.total.toLocaleString()}</td>
                                                <td className="text-center"><button type="button" className="btn btn-sm btn-outline-danger p-1 border-0" onClick={() => removeLine(idx)}><FaTimes size={16}/></button></td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-light">
                                        <td colSpan="5" className="text-end py-3 fw-bold text-muted">MONTANT TOTAL HT :</td>
                                        <td className="text-end fs-5 py-3 fw-bold text-success">{totalHT.toLocaleString()} F</td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center pt-3 mt-3 border-top gap-3">
                            <div>
                                {(!clientSearch || currentLines.length === 0) && (
                                    <span className="small fw-bold text-warning">
                                        <FaExclamationCircle className="me-1"/> Veuillez ajouter un client et une prestation
                                    </span>
                                )}
                            </div>
                            <button 
                                className={`btn px-4 py-2 fw-bold text-white w-100 w-sm-auto ${(!clientSearch || currentLines.length === 0) ? 'btn-secondary' : (editingId ? 'btn-warning' : 'btn-success')}`} 
                                type="submit" 
                                disabled={!clientSearch || currentLines.length === 0}
                                style={(!clientSearch || currentLines.length === 0) ? {} : (!editingId ? { backgroundColor: '#1A7A38', borderColor: '#1A7A38' } : {})}
                            >
                                {editingId ? <><FaSave className="me-2"/> METTRE À JOUR</> : <><FaCheckCircle className="me-2"/> ENREGISTRER PROFORMA</>}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* HISTORIQUE */}
            <div className="card shadow-sm border-0 overflow-hidden">
                <div className="card-header bg-light p-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 border-bottom">
                    <span className="fw-bold text-dark" style={{ letterSpacing: '1px' }}><FaHistory className="me-2 text-success"/>HISTORIQUE DES PROFORMAS</span>
                    <div className="input-group input-group-sm w-100 w-md-auto" style={{maxWidth: '300px'}}>
                        <span className="input-group-text bg-white text-muted border-end-0"><FaSearch /></span>
                        <input className="form-control border-start-0" placeholder="Rechercher n° ou client..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>
                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                    <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                        <thead className="bg-light sticky-top">
                            <tr className="text-uppercase text-muted" style={{ letterSpacing: '1px', fontSize: '0.85rem' }}>
                                <th className="ps-3 py-3 border-bottom-0">N° Proforma</th>
                                <th className="py-3 border-bottom-0">Client</th>
                                <th className="py-3 border-bottom-0">Montant Total</th>
                                <th className="py-3 border-bottom-0">Date Création</th>
                                <th className="pe-3 py-3 text-end border-bottom-0">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredHistory.length === 0 && (
                                <tr><td colSpan="5" className="text-center py-4 text-muted">Aucun historique trouvé.</td></tr>
                            )}
                            {filteredHistory.map(p => (
                                <tr key={p._id}>
                                    <td className="ps-3 fw-bold text-primary">{p.number}</td>
                                    <td className="text-wrap text-dark fw-bold" style={{minWidth:'150px'}}>{p.clientName}</td>
                                    <td className="fw-bold text-success">{p.totalTTC?.toLocaleString()} F</td>
                                    <td className="text-muted small">{new Date(p.createdAt).toLocaleDateString()}</td>
                                    <td className="text-end pe-3">
                                        <button className="btn btn-sm btn-outline-danger rounded-1 py-1 px-2 me-1" onClick={() => handleDownloadPDF(p._id, p.number)} title="Télécharger PDF"><FaFilePdf size={14}/></button>
                                        <button className="btn btn-sm btn-outline-primary rounded-1 py-1 px-2 me-1" onClick={() => {
                                            setEditingId(p._id); 
                                            setClientSearch(p.clientName); 
                                            setCurrentLines(p.lines); 
                                            if (p.date) setDateEmission(new Date(p.date).toISOString().split('T')[0]);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }} title="Modifier"><FaEdit size={14}/></button>
                                        {isAdmin && (
                                            <button className="btn btn-sm btn-outline-secondary rounded-1 py-1 px-2" onClick={() => handleDelete(p._id)} title="Supprimer">
                                                <FaTrash size={14}/>
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ProformaPage;